import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box, Typography, Stack, Chip, Button, Tooltip, IconButton,
  Avatar, ToggleButton, ToggleButtonGroup, Badge,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Select, MenuItem, FormControl, InputLabel, Divider,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import AddIcon from '@mui/icons-material/Add';
import BoltIcon from '@mui/icons-material/Bolt';
import FilterListIcon from '@mui/icons-material/FilterList';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BuildIcon from '@mui/icons-material/Build';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import CircleIcon from '@mui/icons-material/Circle';
import CloseIcon from '@mui/icons-material/Close';
import { BRAND } from '../theme/tokens';

const FI_ORANGE  = '#F57C00';

// ── Colour palettes per job type ───────────────────────────────────────────────
const JOB_TYPES = {
  HVAC:          { label: 'HVAC',          color: '#1565c0', bg: '#e3f2fd', icon: <AcUnitIcon sx={{ fontSize: 11 }} />,         accent: '#1565c0' },
  Electrical:    { label: 'Electrical',    color: '#e65100', bg: '#fff3e0', icon: <ElectricBoltIcon sx={{ fontSize: 11 }} />,   accent: '#f57c00' },
  Plumbing:      { label: 'Plumbing',      color: '#00695c', bg: '#e0f2f1', icon: <WaterDropIcon sx={{ fontSize: 11 }} />,      accent: '#00897b' },
  Maintenance:   { label: 'Maintenance',   color: '#4a148c', bg: '#f3e5f5', icon: <BuildIcon sx={{ fontSize: 11 }} />,          accent: '#7b1fa2' },
  Installation:  { label: 'Installation',  color: '#1b5e20', bg: '#e8f5e9', icon: <BuildIcon sx={{ fontSize: 11 }} />,          accent: '#388e3c' },
} as const;
type JobType = keyof typeof JOB_TYPES;

const JOB_STATUSES = {
  Scheduled:   { color: '#555',    bg: '#f0f0f0',  dot: '#9e9e9e' },
  'In Progress':{ color: '#0277bd', bg: '#e3f2fd', dot: '#1976d2' },
  Completed:   { color: '#2e7d32', bg: '#e8f5e9',  dot: '#43a047' },
  'On Hold':   { color: '#e65100', bg: '#fff3e0',  dot: '#ef6c00' },
  Cancelled:   { color: '#b71c1c', bg: '#ffebee',  dot: '#e53935' },
} as const;
type JobStatus = keyof typeof JOB_STATUSES;

interface Job {
  id: string;
  title: string;
  client: string;
  address: string;
  type: JobType;
  status: JobStatus;
  techId: string;
  startHour: number;
  durationHours: number;
  date: string; // YYYY-MM-DD
  notes?: string;
  priority?: 'high' | 'normal' | 'low';
}

interface Technician {
  id: string;
  name: string;
  role: string;
  initials: string;
  color: string;
  phone?: string;
}

const TECHNICIANS: Technician[] = [
  { id: 't1', name: 'Alex Turner',   role: 'HVAC Lead',           initials: 'AT', color: '#1565c0', phone: '0412 345 678' },
  { id: 't2', name: 'Brooke Nguyen', role: 'Electrician',         initials: 'BN', color: '#e65100', phone: '0413 456 789' },
  { id: 't3', name: 'Chris Patel',   role: 'Plumber',             initials: 'CP', color: '#00695c', phone: '0414 567 890' },
  { id: 't4', name: 'Dana Kim',      role: 'Maintenance Tech',    initials: 'DK', color: '#4a148c', phone: '0415 678 901' },
  { id: 't5', name: 'Evan Walsh',    role: 'HVAC Technician',     initials: 'EW', color: '#1b5e20', phone: '0416 789 012' },
  { id: 't6', name: 'Fiona Shaw',    role: 'Electrician',         initials: 'FS', color: '#880e4f', phone: '0417 890 123' },
];

// ── Generate demo jobs around today ───────────────────────────────────────────
function getWeekStart(d: Date): Date {
  const day = new Date(d);
  const dow = day.getDay();
  day.setDate(day.getDate() - (dow === 0 ? 6 : dow - 1)); // Monday
  day.setHours(0, 0, 0, 0);
  return day;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function fmt(d: Date): string {
  return d.toISOString().split('T')[0];
}

function buildDemoJobs(weekStart: Date): Job[] {
  const d = (offset: number) => fmt(addDays(weekStart, offset));
  return [
    { id: 'J-001', title: 'HVAC Service — Maint 3M',  client: 'Midland Medical',    address: '12 Stirling Hwy, Nedlands',     type: 'HVAC',        status: 'Completed',   techId: 't1', startHour: 8,  durationHours: 2.5, date: d(0), priority: 'normal' },
    { id: 'J-002', title: 'Switchboard Upgrade',       client: 'Retail Collective',  address: '88 Murray St, Perth CBD',       type: 'Electrical',  status: 'In Progress', techId: 't2', startHour: 9,  durationHours: 4,   date: d(0), priority: 'high' },
    { id: 'J-003', title: 'Leak Repair — Kitchen',     client: 'The Grand Hotel',    address: '1 St Georges Tce, Perth',       type: 'Plumbing',    status: 'Scheduled',   techId: 't3', startHour: 10, durationHours: 2,   date: d(0) },
    { id: 'J-004', title: 'AC Installation — Level 3', client: 'Velocity Corp',      address: '45 Colin St, West Perth',       type: 'HVAC',        status: 'Scheduled',   techId: 't1', startHour: 8,  durationHours: 5,   date: d(1), priority: 'high' },
    { id: 'J-005', title: 'Emergency Call-out',        client: 'Apex Logistics',     address: '200 Orrong Rd, Carlisle',       type: 'Electrical',  status: 'In Progress', techId: 't6', startHour: 7,  durationHours: 2,   date: d(1), priority: 'high' },
    { id: 'J-006', title: 'Hot Water System Replace',  client: 'Riverview Apts',     address: '6 Riverside Dr, East Perth',    type: 'Plumbing',    status: 'Scheduled',   techId: 't3', startHour: 8,  durationHours: 3,   date: d(1) },
    { id: 'J-007', title: 'Quarterly Maintenance',     client: 'Southbank Towers',   address: '300 Wellington St, Perth',      type: 'Maintenance', status: 'Scheduled',   techId: 't4', startHour: 9,  durationHours: 6,   date: d(1) },
    { id: 'J-008', title: 'Duct Clean & Inspect',      client: 'Fitzroy Aged Care',  address: '14 Fitzroy St, Fitzroy North',  type: 'HVAC',        status: 'Scheduled',   techId: 't5', startHour: 7,  durationHours: 3,   date: d(1) },
    { id: 'J-009', title: 'Solar Panel Install',       client: 'Green Living Co',    address: '22 Beaufort St, Mt Lawley',     type: 'Installation',status: 'Scheduled',   techId: 't2', startHour: 8,  durationHours: 5,   date: d(2), priority: 'normal' },
    { id: 'J-010', title: 'Fire Alarm Test & Tag',     client: 'Plaza Shopping Ctr', address: '15 Hay St, Perth',              type: 'Electrical',  status: 'Scheduled',   techId: 't6', startHour: 9,  durationHours: 2,   date: d(2) },
    { id: 'J-011', title: 'Split System Service',      client: 'Bayside Medical',    address: '50 Marine Pde, Cottesloe',      type: 'HVAC',        status: 'Completed',   techId: 't1', startHour: 13, durationHours: 2,   date: d(2) },
    { id: 'J-012', title: 'Pump Replacement',          client: 'Water Corp WA',      address: 'Leederville Depot, Perth',      type: 'Plumbing',    status: 'On Hold',     techId: 't3', startHour: 10, durationHours: 4,   date: d(2), priority: 'high' },
    { id: 'J-013', title: 'Preventive Maint — BMS',    client: 'Curtin University',  address: 'Kent St, Bentley WA 6102',      type: 'Maintenance', status: 'Scheduled',   techId: 't4', startHour: 8,  durationHours: 4,   date: d(2) },
    { id: 'J-014', title: 'New AC Unit Install',       client: 'Coles Joondalup',    address: 'Grand Blvd, Joondalup',         type: 'HVAC',        status: 'Scheduled',   techId: 't5', startHour: 9,  durationHours: 3.5, date: d(2) },
    { id: 'J-015', title: 'Stormwater Drain Clear',    client: 'City of Perth',      address: 'Mounts Bay Rd, Perth',          type: 'Plumbing',    status: 'Scheduled',   techId: 't3', startHour: 7,  durationHours: 2,   date: d(3) },
    { id: 'J-016', title: 'UPS Battery Replacement',   client: 'Data Centre WA',     address: '100 Stirling St, Perth',        type: 'Electrical',  status: 'Scheduled',   techId: 't2', startHour: 9,  durationHours: 3,   date: d(3), priority: 'high' },
    { id: 'J-017', title: 'Chiller Service 6M',        client: 'QV1 Building',       address: '250 St Georges Tce, Perth',     type: 'HVAC',        status: 'Scheduled',   techId: 't1', startHour: 8,  durationHours: 5,   date: d(3) },
    { id: 'J-018', title: 'EV Charger Install × 4',    client: 'Westfield Carousel', address: 'Albany Hwy, Cannington',        type: 'Installation',status: 'Scheduled',   techId: 't6', startHour: 7,  durationHours: 6,   date: d(3), priority: 'high' },
    { id: 'J-019', title: 'Annual Safety Inspection',  client: 'Brookfield Office',  address: '123 St Georges Tce, Perth',     type: 'Maintenance', status: 'Scheduled',   techId: 't4', startHour: 10, durationHours: 3,   date: d(4) },
    { id: 'J-020', title: 'Boiler Inspection',         client: 'Fremantle Port Auth',address: '1 Phillimore St, Fremantle',    type: 'Maintenance', status: 'Scheduled',   techId: 't5', startHour: 8,  durationHours: 3,   date: d(4) },
  ];
}

const HOURS = Array.from({ length: 12 }, (_, i) => i + 7); // 7 AM – 6 PM
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ── Component ─────────────────────────────────────────────────────────────────
export const SchedulePage: React.FC = () => {
  const today    = new Date();
  today.setHours(0, 0, 0, 0);
  const [weekStart, setWeekStart] = useState<Date>(getWeekStart(today));
  const [view,      setView]      = useState<'week' | 'day'>('week');
  const [dayView,   setDayView]   = useState<Date>(today);
  const [filterStatus, setFilterStatus] = useState<JobStatus | 'All'>('All');
  const [filterType,   setFilterType]   = useState<JobType | 'All'>('All');
  const [showFilters,  setShowFilters]  = useState(false);
  const [selectedJob,  setSelectedJob]  = useState<Job | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newJob, setNewJob] = useState({ techId: '', type: 'HVAC' as JobType, date: fmt(today), startHour: 9, duration: 2, client: '', title: '', address: '' });

  const jobs = buildDemoJobs(weekStart);

  const weekDays: Date[] = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const dayJobs = (techId: string, date: Date) =>
    jobs.filter(j => j.techId === techId && j.date === fmt(date) &&
      (filterStatus === 'All' || j.status === filterStatus) &&
      (filterType   === 'All' || j.type   === filterType));

  const todayStr = fmt(today);

  // ── Current time indicator ───────────────────────────────────────────────────
  const [nowPct, setNowPct] = useState(0);
  useEffect(() => {
    const calc = () => {
      const h = new Date();
      setNowPct(((h.getHours() - 7) * 60 + h.getMinutes()) / (12 * 60) * 100);
    };
    calc();
    const t = setInterval(calc, 60000);
    return () => clearInterval(t);
  }, []);

  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 80; // scroll to 8am
  }, [view]);

  // ── Job card ─────────────────────────────────────────────────────────────────
  const JobCard = useCallback(({ job }: { job: Job }) => {
    const type  = JOB_TYPES[job.type];
    const stat  = JOB_STATUSES[job.status];
    const topPct   = ((job.startHour - 7) / 12) * 100;
    const heightPct = (job.durationHours / 12) * 100;
    return (
      <Box onClick={() => setSelectedJob(job)}
        sx={{ position: 'absolute', left: 2, right: 2,
          top: `calc(${topPct}% + 1px)`, height: `calc(${heightPct}% - 2px)`,
          borderRadius: 1.5, overflow: 'hidden', cursor: 'pointer',
          borderLeft: `3px solid ${type.accent}`,
          backgroundColor: type.bg,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          transition: 'box-shadow 0.15s, transform 0.1s',
          '&:hover': { boxShadow: '0 3px 10px rgba(0,0,0,0.15)', transform: 'scale(1.01)', zIndex: 10 } }}>
        <Box sx={{ p: '3px 5px', height: '100%', display: 'flex', flexDirection: 'column', gap: 0.2 }}>
          {/* Top row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
            <Box sx={{ color: type.accent, display: 'flex', flexShrink: 0 }}>{type.icon}</Box>
            <Typography sx={{ fontSize: '0.59rem', fontWeight: 800, color: type.accent,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
              {job.id}
            </Typography>
            {job.priority === 'high' && (
              <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#e53935', flexShrink: 0 }} />
            )}
          </Box>
          {/* Title */}
          {heightPct > 8 && (
            <Typography sx={{ fontSize: '0.63rem', fontWeight: 600, color: '#333', lineHeight: 1.2,
              overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
              {job.title}
            </Typography>
          )}
          {/* Client + time */}
          {heightPct > 15 && (
            <>
              <Typography sx={{ fontSize: '0.58rem', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {job.client}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 'auto' }}>
                <AccessTimeIcon sx={{ fontSize: 9, color: '#aaa' }} />
                <Typography sx={{ fontSize: '0.55rem', color: '#aaa' }}>
                  {job.startHour}:00 – {(job.startHour + job.durationHours) % 24}:00
                </Typography>
              </Box>
            </>
          )}
          {/* Status dot */}
          <Box sx={{ position: 'absolute', bottom: 3, right: 4,
            width: 6, height: 6, borderRadius: '50%', backgroundColor: stat.dot }} />
        </Box>
      </Box>
    );
  }, []);

  // ── Stats bar ────────────────────────────────────────────────────────────────
  const weekJobs = jobs.filter(j =>
    (filterStatus === 'All' || j.status === filterStatus) &&
    (filterType   === 'All' || j.type   === filterType));
  const totalScheduled   = weekJobs.length;
  const totalInProgress  = weekJobs.filter(j => j.status === 'In Progress').length;
  const totalCompleted   = weekJobs.filter(j => j.status === 'Completed').length;
  const totalUnassigned  = weekJobs.filter(j => j.status === 'Scheduled').length;

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f8f8fb' }}>

      {/* ── Top bar ── */}
      <Box sx={{ px: 2, py: 1, backgroundColor: '#fff', borderBottom: '1px solid #e8e8ee',
        display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>

        {/* Title + week nav */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: BRAND }}>Schedule</Typography>
          <Typography sx={{ fontSize: '0.7rem', color: '#aaa', mx: 0.5 }}>·</Typography>
          <IconButton size="small" onClick={() => { setWeekStart(prev => addDays(prev, -7)); }}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#333', minWidth: 140, textAlign: 'center' }}>
            {weekStart.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
            {' – '}
            {addDays(weekStart, 6).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
          </Typography>
          <IconButton size="small" onClick={() => setWeekStart(prev => addDays(prev, 7))}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
          <Tooltip title="Jump to today">
            <Button size="small" variant="outlined" startIcon={<TodayIcon />}
              onClick={() => setWeekStart(getWeekStart(today))}
              sx={{ fontSize: '0.65rem', color: BRAND, borderColor: BRAND, py: 0.3, ml: 0.5 }}>
              Today
            </Button>
          </Tooltip>
        </Box>

        {/* View toggle */}
        <ToggleButtonGroup value={view} exclusive onChange={(_e, v) => v && setView(v)} size="small" sx={{ ml: 1 }}>
          <ToggleButton value="week" sx={{ fontSize: '0.65rem', px: 1.5, py: 0.4 }}>Week</ToggleButton>
          <ToggleButton value="day"  sx={{ fontSize: '0.65rem', px: 1.5, py: 0.4 }}>Day</ToggleButton>
        </ToggleButtonGroup>

        {/* Stats chips */}
        <Box sx={{ display: 'flex', gap: 1, ml: 1.5 }}>
          {[
            { label: `${totalScheduled} jobs`,     color: '#555',    bg: '#f0f0f0' },
            { label: `${totalInProgress} active`,  color: '#1976d2', bg: '#e3f2fd' },
            { label: `${totalCompleted} done`,     color: '#2e7d32', bg: '#e8f5e9' },
            { label: `${totalUnassigned} pending`, color: '#888',    bg: '#fafafa' },
          ].map(c => (
            <Chip key={c.label} label={c.label} size="small"
              sx={{ fontSize: '0.62rem', fontWeight: 600, color: c.color, backgroundColor: c.bg, height: 22 }} />
          ))}
        </Box>

        <Box sx={{ flex: 1 }} />

        {/* Filter toggle */}
        <Button size="small" variant={showFilters ? 'contained' : 'outlined'} startIcon={<FilterListIcon />}
          onClick={() => setShowFilters(v => !v)}
          sx={{ fontSize: '0.65rem', color: showFilters ? '#fff' : '#666',
            backgroundColor: showFilters ? '#555' : undefined, borderColor: '#ccc' }}>
          Filters
          {(filterStatus !== 'All' || filterType !== 'All') && (
            <Badge badgeContent="•" color="warning" sx={{ ml: 0.5 }} />
          )}
        </Button>

        {/* Add job */}
        <Button variant="contained" size="small" startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          sx={{ backgroundColor: BRAND, fontSize: '0.72rem', fontWeight: 700 }}>
          Add Job
        </Button>
        <Button variant="outlined" size="small" startIcon={<BoltIcon />}
          sx={{ fontSize: '0.72rem', color: FI_ORANGE, borderColor: FI_ORANGE }}>
          Quick Job
        </Button>
      </Box>

      {/* ── Filter bar ── */}
      {showFilters && (
        <Box sx={{ px: 2, py: 1, backgroundColor: '#fcfcff', borderBottom: '1px solid #eee',
          display: 'flex', gap: 2, alignItems: 'center', flexShrink: 0 }}>
          <Typography sx={{ fontSize: '0.68rem', color: '#888', fontWeight: 600 }}>Status:</Typography>
          <Stack direction="row" spacing={0.5}>
            {(['All', ...Object.keys(JOB_STATUSES)] as (JobStatus | 'All')[]).map(s => (
              <Chip key={s} label={s} size="small" clickable
                onClick={() => setFilterStatus(s)}
                sx={{ fontSize: '0.62rem', height: 22,
                  backgroundColor: filterStatus === s ? BRAND : undefined,
                  color: filterStatus === s ? '#fff' : undefined }} />
            ))}
          </Stack>
          <Divider orientation="vertical" flexItem />
          <Typography sx={{ fontSize: '0.68rem', color: '#888', fontWeight: 600 }}>Type:</Typography>
          <Stack direction="row" spacing={0.5}>
            {(['All', ...Object.keys(JOB_TYPES)] as (JobType | 'All')[]).map(t => (
              <Chip key={t} label={t} size="small" clickable
                onClick={() => setFilterType(t)}
                sx={{ fontSize: '0.62rem', height: 22,
                  backgroundColor: filterType === t ? FI_ORANGE : undefined,
                  color: filterType === t ? '#fff' : undefined }} />
            ))}
          </Stack>
          {(filterStatus !== 'All' || filterType !== 'All') && (
            <Button size="small" variant="text" startIcon={<CloseIcon />}
              onClick={() => { setFilterStatus('All'); setFilterType('All'); }}
              sx={{ fontSize: '0.62rem', color: '#aaa', ml: 'auto' }}>
              Clear
            </Button>
          )}
        </Box>
      )}

      {/* ── Calendar grid ── */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

        {/* ── Week header ── */}
        <Box sx={{ display: 'flex', backgroundColor: '#fff', borderBottom: '2px solid #e8e8ee',
          flexShrink: 0, position: 'sticky', top: 0, zIndex: 20 }}>

          {/* Tech label col */}
          <Box sx={{ width: 160, flexShrink: 0, px: 1.5, py: 1, borderRight: '1px solid #eee',
            display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontSize: '0.62rem', color: '#aaa', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>
              {TECHNICIANS.length} Technicians
            </Typography>
          </Box>

          {/* Day cols */}
          {(view === 'week' ? weekDays : [dayView]).map((day, i) => {
            const isToday = fmt(day) === todayStr;
            const jobCount = jobs.filter(j => j.date === fmt(day) &&
              (filterStatus === 'All' || j.status === filterStatus) &&
              (filterType === 'All' || j.type === filterType)).length;
            return (
              <Box key={i} onClick={() => { if (view === 'week') { setDayView(day); setView('day'); } }}
                sx={{ flex: 1, px: 1, py: 0.8, borderRight: '1px solid #f0f0f0',
                  backgroundColor: isToday ? '#f5f0ff' : 'transparent',
                  cursor: view === 'week' ? 'pointer' : 'default',
                  '&:hover': view === 'week' ? { backgroundColor: isToday ? '#ede5ff' : '#fafafa' } : {} }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700,
                    color: isToday ? BRAND : '#333' }}>
                    {DAY_LABELS[i % 7]}
                  </Typography>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: isToday ? 800 : 400,
                    color: isToday ? BRAND : '#888' }}>
                    {day.getDate()}
                  </Typography>
                  {isToday && (
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: BRAND }} />
                  )}
                </Box>
                {jobCount > 0 && (
                  <Typography sx={{ fontSize: '0.55rem', color: '#aaa' }}>
                    {jobCount} job{jobCount !== 1 ? 's' : ''}
                  </Typography>
                )}
              </Box>
            );
          })}

          {/* Scrollbar spacer */}
          <Box sx={{ width: 8, flexShrink: 0, borderLeft: '1px solid #f0f0f0' }} />
        </Box>

        {/* ── Scrollable body ── */}
        <Box ref={scrollRef} sx={{ flex: 1, overflow: 'auto', display: 'flex' }}>

          {/* ── Left: technician rows ── */}
          <Box sx={{ width: 160, flexShrink: 0, borderRight: '1px solid #eee' }}>
            {/* Hour gutter spacer */}
            <Box sx={{ height: 0 }} />
            {TECHNICIANS.map(tech => (
              <Box key={tech.id}
                sx={{ height: 120, px: 1, py: 1, borderBottom: '1px solid #f5f5f5',
                  display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <Avatar sx={{ width: 24, height: 24, fontSize: '0.6rem', fontWeight: 700,
                    backgroundColor: tech.color }}>{tech.initials}</Avatar>
                  <Box>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#333', lineHeight: 1.2 }}>
                      {tech.name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.58rem', color: '#aaa', lineHeight: 1 }}>
                      {tech.role}
                    </Typography>
                  </Box>
                </Box>
                {/* Job count badge */}
                {(() => {
                  const cnt = jobs.filter(j => j.techId === tech.id &&
                    weekDays.some(d => fmt(d) === j.date) &&
                    (filterStatus === 'All' || j.status === filterStatus) &&
                    (filterType === 'All' || j.type === filterType)).length;
                  return cnt > 0 ? (
                    <Chip label={`${cnt} job${cnt !== 1 ? 's' : ''}`} size="small"
                      sx={{ fontSize: '0.55rem', height: 16, mt: 0.3,
                        backgroundColor: '#f5f0ff', color: BRAND, width: 'fit-content' }} />
                  ) : null;
                })()}
              </Box>
            ))}
          </Box>

          {/* ── Right: time grid ── */}
          <Box sx={{ flex: 1, position: 'relative' }}>

            {/* Hour lines */}
            {HOURS.map(h => (
              <Box key={h} sx={{ position: 'absolute', left: 0, right: 0,
                top: `${((h - 7) / 12) * TECHNICIANS.length * 120}px`,
                height: `${(1 / 12) * TECHNICIANS.length * 120}px`,
                borderTop: h === 7 ? 'none' : '1px dashed #f0f0f0', zIndex: 0 }}>
                {/* This is just background lines — not used directly, rows below handle the layout */}
              </Box>
            ))}

            {/* Technician row grids */}
            {TECHNICIANS.map((tech, ti) => {
              const days = view === 'week' ? weekDays : [dayView];
              return (
                <Box key={tech.id} sx={{ display: 'flex', height: 120,
                  borderBottom: ti < TECHNICIANS.length - 1 ? '1px solid #f0f0f0' : 'none',
                  backgroundColor: ti % 2 === 0 ? '#fff' : '#fdfcff' }}>
                  {days.map((day, di) => {
                    const isToday = fmt(day) === todayStr;
                    const cellJobs = dayJobs(tech.id, day);
                    return (
                      <Box key={di} sx={{ flex: 1, position: 'relative', borderRight: '1px solid #f5f5f5',
                        backgroundColor: isToday ? 'rgba(103,78,167,0.03)' : 'transparent' }}>
                        {/* Hour gridlines inside cell */}
                        {HOURS.map((h, hi) => (
                          <Box key={h} sx={{ position: 'absolute', left: 0, right: 0,
                            top: `${(hi / 12) * 100}%`, borderTop: '1px dashed #f5f5f5',
                            pointerEvents: 'none' }} />
                        ))}
                        {/* Current time line */}
                        {isToday && nowPct >= 0 && nowPct <= 100 && (
                          <Box sx={{ position: 'absolute', left: 0, right: 0, top: `${nowPct}%`,
                            borderTop: '2px solid #e53935', zIndex: 5, pointerEvents: 'none' }}>
                            <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: '#e53935',
                              position: 'absolute', left: 0, top: -3 }} />
                          </Box>
                        )}
                        {/* Job cards */}
                        {cellJobs.map(job => <JobCard key={job.id} job={job} />)}
                      </Box>
                    );
                  })}
                </Box>
              );
            })}

            {/* Time labels on the left edge */}
            <Box sx={{ position: 'absolute', left: -38, top: 0, width: 36, height: '100%', pointerEvents: 'none' }}>
              {HOURS.map((h, i) => (
                <Box key={h} sx={{ position: 'absolute', top: `${(i / 12) * TECHNICIANS.length * 120}px`,
                  right: 0, transform: 'translateY(-50%)' }}>
                  <Typography sx={{ fontSize: '0.55rem', color: '#ccc', textAlign: 'right', fontFamily: 'monospace' }}>
                    {h < 12 ? `${h}am` : h === 12 ? '12pm' : `${h - 12}pm`}
                  </Typography>
                </Box>
              ))}
            </Box>

          </Box>

          {/* Time ruler (left of grid) */}
          <Box sx={{ width: 38, order: -1, flexShrink: 0, position: 'relative', borderRight: '1px solid #eee' }}>
            {TECHNICIANS.map((tech, ti) => (
              <Box key={tech.id} sx={{ height: 120, borderBottom: ti < TECHNICIANS.length - 1 ? '1px solid #f0f0f0' : 'none',
                position: 'relative' }}>
                {HOURS.map((h, hi) => (
                  <Box key={h} sx={{ position: 'absolute', top: `${(hi / 12) * 100}%`, right: 4, transform: 'translateY(-50%)' }}>
                    {ti === 0 && (
                      <Typography sx={{ fontSize: '0.55rem', color: '#ccc', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                        {h < 12 ? `${h}am` : h === 12 ? '12' : `${h - 12}pm`}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>

        </Box>
      </Box>

      {/* ── Job detail dialog ── */}
      <Dialog open={!!selectedJob} onClose={() => setSelectedJob(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}>
        {selectedJob && (() => {
          const type = JOB_TYPES[selectedJob.type];
          const stat = JOB_STATUSES[selectedJob.status];
          const tech = TECHNICIANS.find(t => t.id === selectedJob.techId);
          return (
            <>
              <Box sx={{ px: 2.5, pt: 2, pb: 1.5, borderLeft: `5px solid ${type.accent}`,
                backgroundColor: type.bg, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '0.75rem', color: type.accent }}>
                      {selectedJob.id}
                    </Typography>
                    <Chip label={selectedJob.status} size="small"
                      sx={{ fontSize: '0.6rem', height: 20, backgroundColor: stat.bg, color: stat.dot, fontWeight: 700 }} />
                    <Chip label={selectedJob.type} size="small"
                      sx={{ fontSize: '0.6rem', height: 20, backgroundColor: type.bg, color: type.accent }} />
                    {selectedJob.priority === 'high' && (
                      <Chip label="High Priority" size="small"
                        sx={{ fontSize: '0.6rem', height: 20, backgroundColor: '#ffebee', color: '#e53935' }} />
                    )}
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#222', lineHeight: 1.3 }}>
                    {selectedJob.title}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setSelectedJob(null)}><CloseIcon fontSize="small" /></IconButton>
              </Box>

              <DialogContent sx={{ pt: 2 }}>
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                    {[
                      { icon: <PersonOutlineIcon sx={{ fontSize: 15 }} />, label: 'Technician', value: tech?.name },
                      { icon: <AccessTimeIcon sx={{ fontSize: 15 }} />,    label: 'Time',       value: `${selectedJob.startHour}:00 – ${selectedJob.startHour + selectedJob.durationHours}:00 (${selectedJob.durationHours}h)` },
                      { icon: <LocationOnIcon sx={{ fontSize: 15 }} />,    label: 'Address',    value: selectedJob.address },
                      { icon: <CircleIcon sx={{ fontSize: 15 }} />,        label: 'Client',     value: selectedJob.client },
                    ].map(row => (
                      <Box key={row.label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.8 }}>
                        <Box sx={{ color: '#aaa', mt: 0.2, flexShrink: 0 }}>{row.icon}</Box>
                        <Box>
                          <Typography sx={{ fontSize: '0.6rem', color: '#aaa', lineHeight: 1 }}>{row.label}</Typography>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#333', lineHeight: 1.3 }}>{row.value}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  {/* Assign tech avatar row */}
                  <Box sx={{ pt: 0.5 }}>
                    <Typography sx={{ fontSize: '0.62rem', color: '#aaa', mb: 0.8 }}>ASSIGNED TECHNICIAN</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: '0.7rem', fontWeight: 700,
                        backgroundColor: tech?.color }}>{tech?.initials}</Avatar>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#333' }}>{tech?.name}</Typography>
                        <Typography sx={{ fontSize: '0.62rem', color: '#aaa' }}>{tech?.role} · {tech?.phone}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Stack>
              </DialogContent>

              <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
                <Button variant="outlined" size="small" sx={{ fontSize: '0.7rem' }} onClick={() => setSelectedJob(null)}>Close</Button>
                <Box sx={{ flex: 1 }} />
                <Button variant="outlined" size="small" sx={{ fontSize: '0.7rem', color: '#e65100', borderColor: '#e65100' }}>
                  Reschedule
                </Button>
                <Button variant="contained" size="small"
                  sx={{ backgroundColor: BRAND, fontSize: '0.7rem' }}>
                  Open Job
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* ── Add Job dialog ── */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: '0.9rem', pb: 1 }}>
          Add New Job
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField label="Job title" size="small" fullWidth
              value={newJob.title} onChange={e => setNewJob(p => ({ ...p, title: e.target.value }))} />
            <TextField label="Client / Company" size="small" fullWidth
              value={newJob.client} onChange={e => setNewJob(p => ({ ...p, client: e.target.value }))} />
            <TextField label="Address" size="small" fullWidth
              value={newJob.address} onChange={e => setNewJob(p => ({ ...p, address: e.target.value }))} />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={newJob.type} label="Type" onChange={e => setNewJob(p => ({ ...p, type: e.target.value as JobType }))}>
                  {Object.keys(JOB_TYPES).map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Technician</InputLabel>
                <Select value={newJob.techId} label="Technician" onChange={e => setNewJob(p => ({ ...p, techId: e.target.value }))}>
                  {TECHNICIANS.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField label="Date" type="date" size="small" fullWidth
                value={newJob.date} onChange={e => setNewJob(p => ({ ...p, date: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
              <TextField label="Start hour" type="number" size="small" fullWidth
                inputProps={{ min: 7, max: 18 }}
                value={newJob.startHour} onChange={e => setNewJob(p => ({ ...p, startHour: Number(e.target.value) }))} />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button variant="outlined" size="small" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" size="small" disabled={!newJob.title || !newJob.techId}
            onClick={() => setAddDialogOpen(false)}
            sx={{ backgroundColor: BRAND }}>Add Job</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};
